import {
  DetailType,
  ShotBlockedAttribute,
} from '@collector/sportsapi-client-legacy'
import { ProbableIncident } from '@mobile/ActionQueue/Actions/Incidents/ProbableIncident'

import {
  AttributeConfig,
  DetailConfiguration,
  getAttributeId,
  OnSelectOptions,
} from '../../../../composables/useSportOptions'
import { hasIncidentSomeAttribute } from '../../../../Modals/utils'
import {
  onSelectTechniqueOfShot,
  onSelectTechniqueOfShotDetails,
} from '../../utils'
import { techniqueOfShotDetailsAttributes } from './techniqueOfShotDetailsAttributes'

export enum GroupName {
  Distance = 'Distance',
  TechniqueOfShot = 'Technique of shot',
  TechniqueOfShotDetails = 'Technique of shot - details',
  PressureOfDefenders = 'Pressure of defenders',
  NumberOfDefendersOnTheLineOfShot = 'Number of defenders on the line of shot',
  AdditionalAttributes = 'Additional attributes',
}

export const groups: Map<
  GroupName,
  DetailConfiguration<ShotBlockedAttribute>
> = new Map([
  [
    GroupName.Distance,
    {
      type: DetailType.Button,
      name: GroupName.Distance,
      important: true,
      attributes: [
        ShotBlockedAttribute.InsidePenaltyArea,
        ShotBlockedAttribute.OutsidePenaltyArea,
      ],
      cols: 2,
      multiple: false,
    },
  ],
  [
    GroupName.TechniqueOfShot,
    {
      type: DetailType.Button,
      name: GroupName.TechniqueOfShot,
      important: true,
      attributes: [
        ShotBlockedAttribute.Normal,
        {
          attributeId: ShotBlockedAttribute.FirstTouch,
          isActive: (attributes: ShotBlockedAttribute[]) => {
            const validAttributes = [
              ShotBlockedAttribute.Normal,
              ShotBlockedAttribute.Volley,
            ]
            return attributes.some((attribute) =>
              validAttributes.includes(attribute),
            )
          },
        } as AttributeConfig<ShotBlockedAttribute>,
        ShotBlockedAttribute.Volley,
        ShotBlockedAttribute.Head,
        ShotBlockedAttribute.FreeKick,
        ShotBlockedAttribute.BicycleKick,
        ShotBlockedAttribute.OtherBodyPart,
      ],
      cols: 3,
      multiple: false,
      onSelect: (opts: OnSelectOptions<ShotBlockedAttribute>) =>
        onSelectTechniqueOfShot(opts, ShotBlockedAttribute.FirstTouch),
    },
  ],
  [
    GroupName.TechniqueOfShotDetails,
    {
      type: DetailType.Button,
      name: GroupName.TechniqueOfShotDetails,
      important: true,
      attributes: techniqueOfShotDetailsAttributes,
      cols: 3,
      multiple: false,
      fulfilledWhen: (
        _attributes: ShotBlockedAttribute[],
        probableIncident?: ProbableIncident,
      ) => {
        if (!probableIncident) return false

        const isFulfilled = probableIncident.attribute_ids.some(
          (attribute) =>
            attribute === ShotBlockedAttribute.Head ||
            attribute === ShotBlockedAttribute.OtherBodyPart,
        )

        if (isFulfilled) return true

        return hasIncidentSomeAttribute(
          probableIncident,
          techniqueOfShotDetailsAttributes.map((attribute) =>
            getAttributeId(attribute),
          ),
        )
      },
      onSelect: (opts: OnSelectOptions<ShotBlockedAttribute>) =>
        onSelectTechniqueOfShotDetails(opts, [
          [ShotBlockedAttribute.RightFoot, ShotBlockedAttribute.LeftFoot],
          [ShotBlockedAttribute.LeftFoot, ShotBlockedAttribute.RightFoot],
          [ShotBlockedAttribute.Chip],
        ]),
    },
  ],
  [
    GroupName.PressureOfDefenders,
    {
      type: DetailType.Button,
      name: GroupName.PressureOfDefenders,
      important: true,
      attributes: [
        ShotBlockedAttribute.NoPressure,
        ShotBlockedAttribute.LightPressure,
        ShotBlockedAttribute.HighPressure,
      ],
      cols: 3,
      multiple: false,
    },
  ],
  [
    GroupName.NumberOfDefendersOnTheLineOfShot,
    {
      type: DetailType.Button,
      name: GroupName.NumberOfDefendersOnTheLineOfShot,
      important: true,
      attributes: [
        ShotBlockedAttribute.ClearPosition,
        ShotBlockedAttribute.OneDefender,
        ShotBlockedAttribute.TwoOrMoreDefenders,
      ],
      cols: 3,
      multiple: false,
    },
  ],
  [
    GroupName.AdditionalAttributes,
    {
      type: DetailType.Button,
      name: GroupName.AdditionalAttributes,
      important: false,
      attributes: [
        ShotBlockedAttribute.BigChanceMissed,
        ShotBlockedAttribute.UncomfortablePosition,
        ShotBlockedAttribute.EmptyNet,
        ShotBlockedAttribute.BlockedByTeammate,
      ],
      cols: 3,
      multiple: true,
    },
  ],
])
